import { useEffect, useId, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type PageHeroCardProps = {
  children: ReactNode;
  /** Faded photo on the right inside the card */
  imageSrc?: string;
  /** Optional modern formats for smaller LCP payloads */
  imageAvif?: string;
  imageWebp?: string;
  imageAlt?: string;
  imageObjectPosition?: string;
  /** Mark as LCP candidate: eager load + high fetch priority */
  priority?: boolean;
  /** Extra nodes inside the card (e.g. absolute badges) */
  overlay?: ReactNode;
  className?: string;
  contentClassName?: string;
  /** Tighter top padding for nested pages under floating header */
  compact?: boolean;
};

export const PageHeroCard = ({
  children,
  imageSrc,
  imageAvif,
  imageWebp,
  imageAlt = "",
  imageObjectPosition = "center 30%",
  priority = false,
  overlay,
  className,
  contentClassName,
  compact = false,
}: PageHeroCardProps) => {
  const glowId = useId().replace(/:/g, "");

  // Remove the static HTML LCP shell once the real hero image is in the React tree.
  useEffect(() => {
    if (!priority) return;
    document.getElementById("lcp-shell")?.remove();
  }, [priority]);

  return (
    <section
      className={cn(
        "relative z-10 -mt-[4.25rem] px-2 pb-6 sm:-mt-[4.75rem] sm:px-3 sm:pb-8 lg:px-4",
        className
      )}
    >
      <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl shadow-[0_18px_50px_-28px_hsl(162_52%_8%/0.35)] sm:rounded-3xl">
        <div
          aria-hidden
          className="absolute inset-0 overflow-hidden rounded-2xl sm:rounded-3xl"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_hsl(152_40%_92%)_0%,_transparent_55%),radial-gradient(ellipse_at_bottom_right,_hsl(162_30%_94%)_0%,_transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.28]"
            style={{
              backgroundImage:
                "radial-gradient(hsl(162 52% 8% / 0.09) 1px, transparent 1px)",
              backgroundSize: "18px 18px",
            }}
          />
          <svg
            className="pointer-events-none absolute bottom-0 left-3 right-3 aspect-[1280/740] h-auto w-auto blur-[18px] sm:left-4 sm:right-4"
            viewBox="-40 80 1280 620"
            fill="none"
          >
            <defs>
              <radialGradient
                id={glowId}
                cx="600"
                cy="700"
                r="680"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="hsl(162 52% 8%)" stopOpacity="0.92" />
                <stop offset="16%" stopColor="hsl(162 52% 10%)" stopOpacity="0.75" />
                <stop offset="32%" stopColor="hsl(162 48% 12%)" stopOpacity="0.55" />
                <stop offset="48%" stopColor="hsl(162 45% 14%)" stopOpacity="0.38" />
                <stop offset="64%" stopColor="hsl(162 42% 16%)" stopOpacity="0.24" />
                <stop offset="80%" stopColor="hsl(162 40% 18%)" stopOpacity="0.14" />
                <stop offset="100%" stopColor="hsl(162 38% 20%)" stopOpacity="0.07" />
              </radialGradient>
            </defs>
            <path
              d="M 80 700 C 74.4 691.9 55 668.4 46.1 651.5 C 37.2 634.7 29.5 616.3 26.5 598.9 C 23.5 581.4 23.8 563.1 28.2 546.8 C 32.6 530.4 41.4 514.4 52.8 500.8 C 64.1 487.2 79.9 475.2 96.1 465 C 112.2 454.9 131.8 447.1 149.7 440 C 167.6 432.9 186.9 428.1 203.5 422.4 C 220.2 416.7 236.1 412.4 249.4 405.8 C 262.7 399.3 273.8 392.6 283.2 383.2 C 292.6 373.8 299.3 362.7 305.8 349.4 C 312.4 336.1 316.7 320.2 322.4 303.5 C 328.1 286.9 332.9 267.6 340 249.7 C 347.1 231.8 354.9 212.2 365 196.1 C 375.2 179.9 387.2 164.1 400.8 152.8 C 414.4 141.4 430.4 132.6 446.8 128.2 C 463.1 123.8 481.4 123.5 498.9 126.5 C 516.3 129.5 534.7 137.2 551.5 146.1 C 568.4 155 584.9 168 600 180 C 615.1 192 628.9 206.3 642.2 217.8 C 655.4 229.4 667.2 241.1 679.5 249.3 C 691.8 257.5 703.1 263.8 716 267.3 C 728.8 270.7 741.8 270.9 756.5 270 C 771.3 269 787.3 264.7 804.5 261.3 C 821.8 258 840.9 252.5 860 249.7 C 879.1 246.9 899.9 243.8 918.9 244.6 C 938 245.3 957.7 247.8 974.3 253.9 C 990.9 260 1006.6 269.4 1018.6 281.4 C 1030.6 293.4 1040 309.1 1046.1 325.7 C 1052.2 342.3 1054.7 362 1055.4 381.1 C 1056.2 400.1 1053.1 420.9 1050.3 440 C 1047.5 459.1 1042 478.2 1038.7 495.5 C 1035.3 512.7 1031 528.7 1030 543.5 C 1029.1 558.2 1029.3 571.2 1032.7 584 C 1036.2 596.9 1042.5 608.2 1050.7 620.5 C 1058.9 632.8 1070.6 644.6 1082.2 657.8 C 1093.7 671.1 1113.7 693 1120 700 L 1222 780 L -22 780 Z"
              fill={`url(#${glowId})`}
              shapeRendering="geometricPrecision"
            />
          </svg>
        </div>

        <div
          aria-hidden
          className="absolute inset-0 rounded-2xl border border-white/80 bg-white/50 shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(162,52%,8%,0.04)] backdrop-blur-3xl supports-[backdrop-filter]:bg-white/40 sm:rounded-3xl"
        />

        {imageSrc ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-[min(62%,44rem)] sm:w-[min(55%,50rem)] lg:w-[min(50%,54rem)]"
            style={{
              maskImage: "linear-gradient(to right, transparent 0%, black 38%, black 100%)",
              WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 38%, black 100%)",
            }}
          >
            <picture>
              {imageAvif ? <source srcSet={imageAvif} type="image/avif" /> : null}
              {imageWebp ? <source srcSet={imageWebp} type="image/webp" /> : null}
              <img
                src={imageSrc}
                alt={imageAlt}
                width={1600}
                height={900}
                decoding={priority ? "sync" : "async"}
                fetchPriority={priority ? "high" : "auto"}
                loading={priority ? "eager" : "lazy"}
                className="h-full w-full object-cover opacity-[0.45]"
                style={{ objectPosition: imageObjectPosition }}
              />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-t from-white/50 via-white/15 to-white/25" />
          </div>
        ) : null}

        <div
          className={cn(
            "relative z-10 px-5 sm:px-8 lg:px-12",
            compact
              ? "pb-10 pt-[5.25rem] sm:pb-12 sm:pt-24 lg:pb-14 lg:pt-28"
              : "pb-14 pt-[5.25rem] sm:pb-12 sm:pt-24 lg:pb-14 lg:pt-28",
            contentClassName
          )}
        >
          {children}
        </div>

        {overlay}
      </div>
    </section>
  );
};
